import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, Lock, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(identifier, password);
      if (user.must_change_password) {
        navigate("/changer-mot-de-passe", { replace: true });
      } else {
        navigate(location.state?.from?.pathname || "/", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] flex flex-col" data-testid="login-page">
      <div className="tricolor" />
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="rounded-2xl bg-white dark:bg-[#111827] shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-b from-slate-50 to-white dark:from-[#111827] dark:to-[#0d1420] px-8 pt-8 pb-6 text-center border-b">
              <img src="/logo.png" alt="Logo officiel de la Republique Gabonaise" className="mx-auto w-32 rounded-lg shadow-md ring-2 ring-[#009E60]/20 object-contain bg-white" data-testid="login-logo" />
              <h1 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white" data-testid="login-title">ALARA</h1>
              <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#3A75C4]">Republique Gabonaise</p>
              <p className="mt-2 text-sm font-bold text-slate-800 dark:text-slate-100 leading-snug uppercase tracking-wide" data-testid="login-ministry">
                Ministere des Transports,<br />de la Marine Marchande,<br />charge de la Logistique
              </p>
              <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500 italic">
                Systeme integre de recherche et de gestion
              </p>
            </div>

            <form onSubmit={submit} className="px-8 py-6 space-y-4" data-testid="login-form">
              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-900 px-4 py-3 text-sm text-red-700 dark:text-red-300" data-testid="login-error" role="alert">
                  {error}
                </div>
              )}
              <div className="space-y-1.5">
                <label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Identifiant</label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                    className="pl-9 h-11" placeholder="Votre identifiant" autoComplete="username"
                    required data-testid="login-identifier" />
                </div>
              </div>
              <div className="space-y-1.5">
                <label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Mot de passe</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                    className="pl-9 pr-10 h-11" placeholder="Votre mot de passe" autoComplete="current-password"
                    required data-testid="login-password" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="login-toggle-password" aria-label="Afficher le mot de passe">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading}
                className="w-full h-11 bg-[#009E60] hover:bg-[#00804e] text-white font-semibold text-sm"
                data-testid="login-submit">
                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Connexion en cours...</> : "SE CONNECTER"}
              </Button>
              <p className="text-center text-[11px] text-slate-400">
                Acces reserve aux agents habilites. Toute connexion est journalisee.
              </p>
            </form>
          </div>
          <p className="mt-4 text-center text-[10px] tracking-[0.3em] text-slate-500">UNION - TRAVAIL - JUSTICE</p>
        </div>
      </div>
    </div>
  );
}
